import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { ThemeService } from '../services/theme.service';
import { InvoiceStatusCounts, MemberStatusCounts, PricingTierCounts } from '../models/ui/dashboard.model';

export interface DashboardChartConfig {
	type: 'doughnut' | 'bar';
	data: ChartData<'doughnut'> | ChartData<'bar'>;
	options: ChartOptions<'doughnut'> | ChartOptions<'bar'>;
	showCenterTotal?: boolean;
	height?: number;
}

export class DashboardChartUtilities {

	//#region Chart Color Helpers

	private static getChartColors(themeService: ThemeService): { success: string; danger: string; warning: string; info: string; secondary: string; primary: string } {
		const isDark = themeService.isDarkThemeActive();
		return {
			success: isDark ? '#75b798' : '#198754',
			danger: isDark ? '#ea868f' : '#dc3545',
			warning: isDark ? '#ffda6a' : '#ffc107',
			info: isDark ? '#6edff6' : '#0dcaf0',
			secondary: isDark ? '#a7acb1' : '#6c757d',
			primary: isDark ? '#6ea8fe' : '#0d6efd'
		};
	}

	private static getForegroundColor(themeService: ThemeService): string {
		return themeService.isDarkThemeActive() ? '#adb5bd' : '#495057';
	}

	private static getDonutBorderColor(themeService: ThemeService): string {
		return themeService.isDarkThemeActive() ? '#495057' : '#ffffff';
	}

	//#endregion

	//#region Donut Chart Builders

	static buildInvoiceStatusChartData(counts: InvoiceStatusCounts, themeService: ThemeService): DashboardChartConfig {
		const colors = this.getChartColors(themeService);
		return this.buildDonutConfig(
			['Open', 'Late', 'Paid', 'Voided'],
			[counts.open, counts.late, counts.paid, counts.voided],
			[colors.info, colors.danger, colors.success, colors.secondary],
			'Invoices',
			themeService
		);
	}

	static buildMemberStatusChartData(counts: MemberStatusCounts, themeService: ThemeService): DashboardChartConfig {
		const colors = this.getChartColors(themeService);
		const labels = ['Created', 'Invited', 'Expired', 'Active', 'Deactivated'];
		const data = [counts.created, counts.invited, counts.expired, counts.active, counts.deactivated];
		const backgroundColors = [colors.info, colors.primary, colors.warning, colors.success, colors.secondary];

		if (counts.deleted !== undefined) {
			labels.push('Deleted');
			data.push(counts.deleted);
			backgroundColors.push(colors.danger);
		}

		return this.buildDonutConfig(labels, data, backgroundColors, 'Members', themeService);
	}

	private static buildDonutConfig(
		labels: string[],
		data: number[],
		backgroundColors: string[],
		entityLabel: string,
		themeService: ThemeService
	): DashboardChartConfig {
		const foreground = this.getForegroundColor(themeService);
		const border = this.getDonutBorderColor(themeService);
		return {
			type: 'doughnut',
			showCenterTotal: true,
			data: {
				labels,
				datasets: [{
					data,
					backgroundColor: backgroundColors,
					borderColor: border,
					borderWidth: 1
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: '60%',
				color: foreground,
				plugins: {
					legend: {
						position: 'right',
						labels: {
							color: foreground,
							font: { size: 13 },
							boxWidth: 12,
							boxHeight: 12
						}
					},
					tooltip: {
						displayColors: true,
						callbacks: {
							title: () => '',
							label: (ctx: TooltipItem<'doughnut'>) => ` ${ctx.parsed} ${ctx.label} ${entityLabel}`
						}
					}
				}
			}
		};
	}

	//#endregion

	//#region Bar Chart Builders

	static buildPricingTierChartData(counts: PricingTierCounts, themeService: ThemeService): DashboardChartConfig {
		const colors = this.getChartColors(themeService);
		const foreground = this.getForegroundColor(themeService);
		const categories = ['Standard', 'Premium', 'White Glove', 'Custom', 'Trial'];
		const barColors = [colors.primary, colors.success, colors.warning, colors.info, colors.secondary];
		const gridColor = themeService.isDarkThemeActive() ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

		return {
			type: 'bar',
			height: 300,
			data: {
				labels: categories,
				datasets: [{
					label: 'Organizations',
					data: [counts.standard, counts.premium, counts.whiteGlove, counts.custom, counts.trial],
					backgroundColor: barColors,
					borderRadius: 4,
					borderSkipped: false
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				color: foreground,
				scales: {
					x: {
						grid: { display: false },
						ticks: { color: foreground, font: { size: 12 } }
					},
					y: {
						beginAtZero: true,
						grid: { color: gridColor },
						ticks: {
							color: foreground,
							font: { size: 12 },
							precision: 0
						}
					}
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						displayColors: true,
						callbacks: {
							title: () => '',
							label: (ctx: TooltipItem<'bar'>) => ` ${ctx.parsed.y} ${ctx.label} Organizations`
						}
					}
				}
			}
		};
	}

	//#endregion
}
